import * as React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { IconTrash } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import { PurgeDialogDispatch } from '~teacher/components/quizzes/PurgeDialog';
import {
  isOverFolderLimit,
  isOverQuizLimit,
} from '~teacher/components/workspaces/utilities/checkWorkspaceItemLimit';
import { groupWorkspaceItemsByType } from '~teacher/components/workspaces/utilities/groupWorkspaceItemsByType';
import { MAX_WORKSPACE_ITEMS } from '~teacher/components/workspaces/WorkspaceQuizzes/actions/constants';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { usePurgeWorkspaceFolders } from '~teacher/hook/workspaces/usePurgeWorkspaceFolders';
import { usePurgeWorkspaceQuizzes } from '~teacher/hook/workspaces/usePurgeWorkspaceQuizzes';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

interface UsePurgeWorkspaceItemActionParams {
  workspaceId: string;
  purgeDialogDispatch: PurgeDialogDispatch;
  paywallCallback: () => void;
}

export function usePurgeWorkspaceItemAction({
  workspaceId,
  purgeDialogDispatch,
  paywallCallback,
}: UsePurgeWorkspaceItemActionParams): Action<WorkspaceItem> {
  const purgeWorkspaceFoldersMutation = usePurgeWorkspaceFolders();
  const purgeWorkspaceQuizzesMutation = usePurgeWorkspaceQuizzes();
  const {
    permissions: {
      purgeWorkspaceFolder: canPurgeWorkspaceFolder,
      purgeQuiz: canPurgeQuiz,
    },
  } = useWorkspacePermissionsV2({ workspaceId });

  return React.useMemo(
    () =>
      ({
        disabled: ({ data }) => {
          if (!data) return true;
          const { quiz, folder } = groupWorkspaceItemsByType(data);

          return (
            (quiz.length >= 1 && !canPurgeQuiz) ||
            (folder.length >= 1 && !canPurgeWorkspaceFolder)
          );
        },
        action: ({ data, close }) => {
          const { folder, quiz } = groupWorkspaceItemsByType(data);

          if (isOverFolderLimit(folder) || isOverQuizLimit(quiz)) {
            toast.critical(
              translate('Please select less than {0} items').format(
                MAX_WORKSPACE_ITEMS
              )
            );
            close();
            return;
          }

          const confirmAction = () => {
            const workspaceQuizIds = quiz.map((quiz) => quiz.id);
            if (workspaceQuizIds.length > 0) {
              purgeWorkspaceQuizzesMutation.mutate(
                {
                  workspaceId: workspaceId,
                  workspaceQuizIds: workspaceQuizIds,
                },
                {
                  onError: () => {
                    toast.critical(
                      translate(
                        'An error occurred while trying to permanently delete'
                      )
                    );
                  },
                }
              );
            }

            const workspaceFolderIds = folder.map((folder) => folder.id);
            if (workspaceFolderIds.length > 0) {
              purgeWorkspaceFoldersMutation.mutate(
                {
                  workspaceId: workspaceId,
                  workspaceFolderIds: workspaceFolderIds,
                },
                {
                  onError: () => {
                    toast.critical(
                      translate(
                        'An error occurred while trying to permanently delete'
                      )
                    );
                  },
                }
              );
            }
            purgeDialogDispatch({ type: 'close' });
          };

          purgeDialogDispatch({
            type: 'open',
            payload: {
              selectedItemCount: data.length,
              confirmAction: confirmAction,
            },
          });

          close();
          return;
        },
        icon: <IconTrash />,
        key: 'purge-items',
        title: translate('Delete Forever'),
        color: 'text-red-700',
        paywallCallback:
          !canPurgeQuiz && !canPurgeWorkspaceFolder ? paywallCallback : null,
        showProBadge: !canPurgeQuiz && !canPurgeWorkspaceFolder,
      } as Action<WorkspaceItem>),
    [
      paywallCallback,
      canPurgeQuiz,
      canPurgeWorkspaceFolder,
      purgeDialogDispatch,
      purgeWorkspaceQuizzesMutation,
      workspaceId,
      purgeWorkspaceFoldersMutation,
    ]
  );
}
